import { Button, MenuItem, Select, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useAlenSchedulingContext } from "../AlenSchedulingContext";
import { SchedulableAction } from "../types";

function ActionSelector() {
  const { setNewScheduling, newScheduling, commonActions } =
    useAlenSchedulingContext();

  let selectableOptions:
    | SchedulableAction[keyof SchedulableAction][]
    | undefined;

  if (newScheduling.actionCode) {
    selectableOptions = commonActions.find(
      (a) => a.standardName === newScheduling.actionCode
    )?.options;
  }

  useEffect(() => {
    if (newScheduling.actionCode !== undefined) {
      return;
    }
    if (commonActions.length > 0) {
      setNewScheduling((prev) => ({
        ...prev,
        actionCode: commonActions[0].standardName,
        schedulerCode: commonActions[0].standardName.toUpperCase(),
      }));
    }
  }, [commonActions, newScheduling.actionCode, setNewScheduling]);

  useEffect(() => {
    if (selectableOptions === undefined) return;
    if (selectableOptions.length === 0) return;
    if (
      newScheduling.actionStartValue !== undefined &&
      selectableOptions.includes(newScheduling.actionStartValue)
    )
      return;
    setNewScheduling((prev) => ({
      ...prev,
      actionStartValue: selectableOptions ? selectableOptions[0] : undefined,
    }));
  }, [
    selectableOptions,
    newScheduling.actionCode,
    setNewScheduling,
    newScheduling.actionStartValue,
  ]);
  const [enableEndAction, setEnableEndAction] = useState(false);
  const [endActionOptions, setEndActionOptions] = useState<
    SchedulableAction[keyof SchedulableAction][] | undefined
  >();
  useEffect(() => {
    if (enableEndAction) {
      if (newScheduling.actionCode && selectableOptions) {
        setEndActionOptions(
          selectableOptions.filter(
            (option) => option !== newScheduling.actionStartValue
          )
        );
      }
    } else {
      setNewScheduling((prev) => ({
        ...prev,
        actionEndValue: undefined,
        actionEndTime: undefined,
      }));
    }
  }, [
    newScheduling.actionCode,
    setEndActionOptions,
    newScheduling.actionStartValue,
    selectableOptions,
    enableEndAction,
    setNewScheduling,
  ]);
  useEffect(() => {
    if (endActionOptions !== undefined) {
      setNewScheduling((prev) => ({
        ...prev,
        actionEndValue: endActionOptions[0],
      }));
    }
  }, [endActionOptions, setNewScheduling]);
  return (
    <>
      <Stack direction="row" gap="1rem">
        <Select
          sx={{ width: "6rem" }}
          onChange={(e) => {
            setNewScheduling((prev) => ({
              ...prev,
              actionCode: e.target.value as keyof SchedulableAction,
              schedulerCode: e.target.value.toUpperCase(),
              actionStartValue: undefined,
            }));
          }}
          value={newScheduling.actionCode || ""}
        >
          {commonActions.map((action) => (
            <MenuItem key={action.standardName} value={action.standardName}>
              {action.standardName}
            </MenuItem>
          ))}
        </Select>
        {selectableOptions && (
          <Select
            onChange={(e) => {
              setNewScheduling((prev) => ({
                ...prev,
                actionStartValue: e.target
                  .value as SchedulableAction[keyof SchedulableAction],
              }));
            }}
            value={newScheduling.actionStartValue || ""}
          >
            {selectableOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        )}
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker
            value={
              newScheduling.actionStartTime
                ? moment(newScheduling.actionStartTime, "hh:mm A")
                : null
            }
            label={`Time (${newScheduling.timezone})`}
            onChange={(time) => {
              if (time) {
                setNewScheduling((prev) => ({
                  ...prev,
                  actionStartTime: time.format("hh:mm A"),
                }));
              }
            }}
          />
        </LocalizationProvider>
      </Stack>

      {!enableEndAction ? (
        <Button
          onClick={() => setEnableEndAction(true)}
          color="primary"
          variant="contained"
        >
          Add End Action
        </Button>
      ) : (
        <Stack direction="row" gap="1rem" alignItems="center">
          <Typography variant="body1" width="6rem">
            And then
          </Typography>

          <Select
            onChange={(e) => {
              setNewScheduling((prev) => ({
                ...prev,
                actionEndValue: e.target
                  .value as SchedulableAction[keyof SchedulableAction],
              }));
            }}
            value={newScheduling.actionEndValue || ""}
          >
            {(endActionOptions ?? []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              label={`Time (${newScheduling.timezone})`}
              onChange={(time) => {
                if (time) {
                  setNewScheduling((prev) => ({
                    ...prev,
                    actionEndTime: time.format("hh:mm A"),
                  }));
                }
              }}
            />
          </LocalizationProvider>
          <Button
            color="danger"
            variant="outlined"
            onClick={() => {
              setEnableEndAction(false);
            }}
          >
            Clear
          </Button>
        </Stack>
      )}
    </>
  );
}

export default ActionSelector;
